import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Upload, Checkbox, message, Avatar, Row, Col, Spin, Card, Typography, Divider } from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  LockOutlined,
  CameraOutlined,
  EditOutlined
} from '@ant-design/icons';
import './index.css'; 
import profile from '../../services/profile';
import PageLayout from '../../components/layout/Layout';
import env from '../../env';

const { Title, Text } = Typography;

const ProfilePage = () => {
  const [form] = Form.useForm();
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [profileImageName, setProfileImageName] = useState(null);
  const [changePassword, setChangePassword] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageloading, setImageLoading] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        const data = await profile.get();
        setLoading(false);
        form.setFieldsValue(data.data);
        if (data.data.profile_picture) {
          console.log(`${env.BACKEND_STORAGE_URL}/${data.data.profile_picture}`);
          setProfileImageUrl(`${env.BACKEND_STORAGE_URL}/${data.data.profile_picture}`);
        }
      } catch (error) {
        setLoading(false);
        message.error('Failed to fetch profile data!');
      }
    };

    fetchProfileData();
  }, [form]);

  const handleFinish = async (values) => {
    setBtnLoading(true);
    try {
      const payload = {
        name: values.name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
      };
      if (changePassword) {
        payload.current_password = values.current_password;
        payload.password = values.new_password;
      }
      await profile.update(payload);
      form.setFieldsValue({
        current_password: undefined,
        new_password: undefined,
      });
      setChangePassword(false);
      message.success('Profile updated successfully!');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update profile!');
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUploadChange = async (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    try {
      console.log("info: ", info);
      const response = await profile.uploadPicture(info.file.originFileObj);
      setProfileImage(info.file.originFileObj);
      setProfileImageUrl(URL.createObjectURL(info.file.originFileObj));
      setProfileImageName(info.file.name);
      message.success(`${info.file.name} file uploaded successfully`);
    } catch (error) {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const uploadProps = {
    name: 'profile_picture',
    accept: 'image/*',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append('profile_picture', file);
      try {
        setImageLoading(true);
        const response = await profile.uploadPicture(formData);
        setImageLoading(false);
        console.log("response: ", response);
        onSuccess(null, file);
      } catch (error) {
        setImageLoading(false);
        onError(error);
      }
    },
    onChange: handleUploadChange,
  };

  const imgCropProps = {
    movable: false,
    showReset: true,
    minZoom: 0.5,
    maxZoom: 2,
    maxAspect: 4/3,
    zoomSlider: true,
    rotationSlider: true,
    cropShape: 'rect',
    modalTitle: 'Edit Image',
    modalWidth: 800,
    modalOk: 'Save',
    modalCancel: 'Cancel',
  };

  const toggleChangePassword = (e) => {
    setChangePassword(e.target.checked);
  };

  return (
    <PageLayout>
      <div className="profile-container">
        <Spin spinning={loading}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div className="page-header">
              <Title level={2}>Profile Settings</Title>
              <Text type="secondary">
                Manage your personal information and account settings
              </Text>
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={8} lg={6}>
            <Card className="profile-media-card">
              <div className="profile-photo-section">
                <Title level={5}>Profile Photo</Title>
                <Text type="secondary" className="upload-hint">
                  Upload a professional profile picture
                </Text>
                
                <div className="avatar-container">
                  <Spin spinning={imageloading}>
                    {profileImageUrl ? (
                      <Avatar
                        src={profileImageUrl}
                        size={200}
                        icon={<UserOutlined />}
                        className="profile-avatar"
                      />
                    ) : (
                      <div className="upload-placeholder">
                        <UserOutlined />
                        <Text>No Photo</Text>
                      </div>
                    )}
                    <div className="avatar-overlay">
                      <Upload {...uploadProps}>
                        <Button 
                          type="text" 
                          icon={<CameraOutlined />}
                          className="upload-button"
                        >
                          Change Photo
                        </Button>
                      </Upload>
                    </div>
                  </Spin>
                </div>
              </div>

              <Divider />

              <div className="profile-info-section">
                <Title level={5}>Account Info</Title>
                <div className="info-item">
                  <Text type="secondary">Member since</Text>
                  <Text strong>January 2024</Text>
                </div>
                
              </div>
            </Card>
          </Col>

          <Col xs={24} md={16} lg={18}>
            <Card className="profile-details-card">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="profile-form"
              >
                <div className="form-section">
                  <Title level={5}>Personal Information</Title>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                      >
                        <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                          { required: true, message: 'Please enter your email' },
                          { type: 'email', message: 'Please enter a valid email' }
                        ]}
                      >
                        <Input prefix={<MailOutlined />} placeholder="Enter your email" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="phone"
                        label="Phone Number"
                      >
                        <Input prefix={<PhoneOutlined />} placeholder="Enter your phone number" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <Divider />

                <div className="form-section">
                  <Title level={5}>Password Settings</Title>
                  <div className="password-toggle">
                    <Checkbox 
                      checked={changePassword}
                      onChange={toggleChangePassword}
                    >
                      Change Password
                    </Checkbox>
                  </div>

                  {changePassword && (
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="current_password"
                          label="Current Password"
                          rules={[{ required: true, message: 'Please enter current password' }]}
                        >
                          <Input.Password prefix={<LockOutlined />} placeholder="Enter current password" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="new_password"
                          label="New Password"
                          rules={[
                            { required: true, message: 'Please enter new password' },
                            { min: 8, message: 'Password must be at least 8 characters' },
                          ]}
                        >
                          <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>

                <Divider />

                <div className="form-actions">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={btnLoading}
                    size="large"
                    icon={<EditOutlined />}
                  >
                    Update Profile
                  </Button>
                  <Button 
                    size="large"
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
        </Spin>
      </div>
    </PageLayout>
  );
};

export default ProfilePage;
